/**
 * feedback-bubble.tsx — Gửi Feedback. Điện thoại: tab icon con bọ ép mép, giữa màn hình, LUÔN NỔI (gắn trong AppLayout). Máy tính: nút nằm cố định
 * trong menu trái (side-nav) — bản rộng khi menu ghim / mở, chỉ icon khi thu gọn; hộp góp ý `FeedbackDialog` dùng chung.
 *
 * Bấm → hộp góp ý: chọn Báo lỗi / Góp ý / Order tính năng, gõ nội dung, (tuỳ chọn) đính kèm thông tin kỹ thuật
 * (trang đang mở, trình duyệt, vài lỗi gần nhất — xem lib/client-diagnostics.ts). Máy chủ ghi mỗi góp ý ra file để đội phát
 * triển theo dõi và sửa (backend: modules/feedback).
 *
 * Kéo thả được (không che nút Gửi của khung chat…): thả ra thì tự dính cạnh trái / phải, nhớ vị trí ở trình duyệt.
 */
import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { ImagePlus, Loader2, MessageSquareHeart, Send, X } from 'lucide-react'
import { BugIcon } from './bug-icon'
import { Button } from '@/components/ui/button'
import { Checkbox, Textarea } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useSendFeedback } from '@/hooks/use-feedback'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { apiError } from '@/lib/api-client'
import { getDiagnostics } from '@/lib/client-diagnostics'
import { MAX_IMAGES, imagesFromClipboard, shrinkImage } from '@/lib/feedback-images'
import {
  FEEDBACK_TYPE_OPTIONS, MESSAGE_MAX, browserLabel, buildFeedbackPayload, canSendFeedback, type FeedbackType,
} from '@/lib/feedback'
import { BUBBLE_SIZE, EDGE_GAP, clampBottom, loadPos, savePos, snapPosition, type BubblePos } from '@/lib/feedback-bubble-pos'
import { cn } from '@/lib/utils'

/** Kéo quá ngưỡng này (px) mới tính là kéo; ngắn hơn là bấm. */
const DRAG_THRESHOLD = 6

export function FeedbackBubble() {
  const { t } = useTranslation('sharedMisc')
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()
  const mobile = useIsMobile()

  // Vị trí đã chọn (null = chưa kéo lần nào → dùng vị trí mặc định theo màn hình). `live` = toạ độ góc trái-trên khi đang kéo.
  const [pos, setPos] = useState<BubblePos | null>(() => loadPos(mobile))
  const [live, setLive] = useState<{ x: number; y: number } | null>(null)
  const drag = useRef<{ dx: number; dy: number; sx: number; sy: number; moved: boolean } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  // Trình duyệt vẫn bắn `click` sau khi thả chuột/ngón tay dù vừa kéo → bỏ qua click đó, không mở hộp.
  const justDragged = useRef(false)

  // Đổi giữa máy tính ↔ điện thoại (xoay máy, kéo cửa sổ) → nạp vị trí đã nhớ của chế độ đó.
  useEffect(() => { setPos(loadPos(mobile)) }, [mobile])

  // Xoay màn hình / đổi cỡ cửa sổ → kéo nút trở lại trong màn hình.
  useEffect(() => {
    const onResize = () => setPos((p) => (p ? { ...p, bottom: clampBottom(p.bottom, window.innerHeight) } : p))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    justDragged.current = false
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, sx: e.clientX, sy: e.clientY, moved: false }
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current
    if (!d) return
    if (!d.moved && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < DRAG_THRESHOLD) return
    d.moved = true
    setLive({ x: e.clientX - d.dx, y: e.clientY - d.dy })
  }
  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current
    drag.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    if (!d?.moved) return
    justDragged.current = true
    const next = snapPosition(e.clientX - d.dx, e.clientY - d.dy, window.innerWidth, window.innerHeight, e.currentTarget.getBoundingClientRect().width)
    setLive(null)
    setPos(next)
    savePos(next, mobile)
  }

  // Máy tính: nút nổi sát mép phải, cao hơn nút "Tiếp tục thiết lập" (góc phải-dưới) để hai nút không đè nhau.
  // Điện thoại: "tab" ép vào mép màn hình, GIỮA chiều cao — chỉ lòi ra một nửa hình tròn nhưng vẫn thấy trọn icon;
  // kéo lên xuống để dời, kéo sang nửa kia thì ép vào mép bên kia. Đang kéo thì hiện trọn hình tròn.
  const docked = mobile && !live
  const side = pos?.side ?? 'right'
  const edge = docked ? -BUBBLE_SIZE / 2 : EDGE_GAP
  const defaultBottom = mobile ? `calc(50dvh - ${BUBBLE_SIZE / 2}px)` : '9rem'
  const style: React.CSSProperties = live
    ? { left: live.x, top: live.y }
    : { [side]: edge, bottom: pos ? pos.bottom : defaultBottom }

  // Máy tính: nút nằm trong menu trái (side-nav), không nổi nữa.
  if (!mobile) return null

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={t('feedbackBubble.ariaLabel')}
        title={t('feedbackBubble.title')}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { drag.current = null; setLive(null) }}
        onClick={() => {
          if (justDragged.current) { justDragged.current = false; return }
          setOpen(true)
        }}
        style={{ ...style, height: BUBBLE_SIZE, ...(mobile ? { width: BUBBLE_SIZE } : {}) }}
        className={cn(
          'group fixed z-40 flex touch-none select-none items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground',
          // Máy tính: viên thuốc có chữ. Điện thoại: hình tròn ép mép — icon nằm trong nửa còn thấy (nửa kia nằm ngoài màn hình).
          !mobile && 'gap-1.5 pl-3.5 pr-4 hover:scale-105 hover:shadow-xl',
          mobile && 'p-0',
          docked && (side === 'right' ? 'justify-start pl-[3px]' : 'justify-end pr-[3px]'),
          'shadow-lg ring-4 ring-background/70 transition-[transform,box-shadow] active:scale-95',
          'focus-visible:outline-none focus-visible:ring-primary/40',
          live && 'cursor-grabbing scale-110',
          !live && 'animate-card-in',
        )}
      >
        <BugIcon className="h-5 w-5 shrink-0" />
      </button>
      <FeedbackDialog open={open} onOpenChange={setOpen} pathname={pathname} />
    </>
  )
}

export function FeedbackDialog({ open, onOpenChange, pathname }: { open: boolean; onOpenChange: (v: boolean) => void; pathname: string }) {
  const { t } = useTranslation('sharedMisc')
  const [type, setType] = useState<FeedbackType>('bug')
  const [message, setMessage] = useState('')
  const [withDiagnostics, setWithDiagnostics] = useState(true)
  const [images, setImages] = useState<string[]>([])
  const fileRef = useRef<HTMLInputElement>(null)
  const send = useSendFeedback()
  const opt = FEEDBACK_TYPE_OPTIONS.find((o) => o.value === type) ?? FEEDBACK_TYPE_OPTIONS[0]

  // Tóm tắt "sẽ gửi gì" — đọc lại mỗi lần mở hộp để số lỗi luôn mới.
  const diag = open ? getDiagnostics() : { errors: [], apiFailures: [] }
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const viewport = typeof window !== 'undefined' ? `${window.innerWidth}×${window.innerHeight}` : ''
  const nErr = diag.errors.length + diag.apiFailures.length

  // Thêm ảnh (chọn file / dán ảnh chụp màn hình): thu nhỏ rồi giữ trong bộ nhớ; quá số lượng thì báo.
  const addImages = async (files: File[]) => {
    if (!files.length) return
    const room = MAX_IMAGES - images.length
    if (room <= 0) { toast.error(t('feedbackBubble.maxImages', { max: MAX_IMAGES })); return }
    if (files.length > room) toast.info(t('feedbackBubble.moreImages', { room, max: MAX_IMAGES }))
    const done: string[] = []
    for (const f of files.slice(0, room)) {
      try { done.push(await shrinkImage(f)) } catch { toast.error(t('feedbackBubble.imageReadError')) }
    }
    if (done.length) setImages((cur) => [...cur, ...done].slice(0, MAX_IMAGES))
  }

  const submit = async () => {
    if (!canSendFeedback(message) || send.isPending) return
    const payload = buildFeedbackPayload(
      { type, message, withDiagnostics, images },
      { pathname, userAgent: ua, viewportW: window.innerWidth, viewportH: window.innerHeight },
      getDiagnostics(),
      (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '',
    )
    try {
      await send.mutateAsync(payload)
      toast.success(t('feedbackBubble.thankYou'))
      setMessage('')
      setImages([])
      onOpenChange(false)
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-3 p-5">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            <MessageSquareHeart className="h-5 w-5 text-primary" /> {t('feedbackBubble.dialogTitle')}
          </DialogTitle>
          <DialogDescription className="rounded-lg bg-primary/5 p-2.5 text-[13px] leading-relaxed text-foreground/80">
            <Trans t={t} i18nKey="feedbackBubble.dialogDescription" components={{ b1: <strong />, b2: <strong />, b3: <strong /> }} />
          </DialogDescription>
        </DialogHeader>

        <div role="radiogroup" aria-label={t('feedbackBubble.typeAriaLabel')} className="grid grid-cols-3 gap-1.5">
          {FEEDBACK_TYPE_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={type === o.value}
              onClick={() => setType(o.value)}
              className={cn(
                'rounded-lg border px-2 py-2 text-center text-[13px] transition-colors',
                type === o.value ? 'border-primary bg-primary/10 font-medium text-primary' : 'text-muted-foreground hover:border-primary/50',
              )}
            >
              <span className="block text-base leading-none">{o.emoji}</span>
              <span className="mt-1 block">{o.label}</span>
            </button>
          ))}
        </div>

        <div>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void submit() }}
            onPaste={(e) => {
              const files = imagesFromClipboard(e.clipboardData)
              if (files.length) { e.preventDefault(); void addImages(files) }
            }}
            placeholder={opt.placeholder}
            rows={5}
            maxLength={MESSAGE_MAX}
            aria-label={t('feedbackBubble.messageAriaLabel')}
            className="resize-none"
          />
          <p className="mt-1 text-right text-[11px] text-muted-foreground">{message.length}/{MESSAGE_MAX}</p>
        </div>

        {/* Ảnh chụp màn hình: dán (Ctrl/⌘+V) vào ô trên hoặc chọn file — thu nhỏ trước khi gửi. */}
        <div>
          <div className="flex flex-wrap items-center gap-2">
            {images.map((src, i) => (
              <div key={i} className="relative h-14 w-14 overflow-hidden rounded-md border">
                <img src={src} alt={t('feedbackBubble.attachedImageAlt', { n: i + 1 })} className="h-full w-full object-cover" />
                <button
                  type="button"
                  aria-label={t('feedbackBubble.removeImageAriaLabel', { n: i + 1 })}
                  onClick={() => setImages((cur) => cur.filter((_, j) => j !== i))}
                  className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-black/60 text-white"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {images.length < MAX_IMAGES && (
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                <ImagePlus /> {t('feedbackBubble.attachImage')}
              </Button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => { void addImages([...(e.target.files ?? [])]); e.target.value = '' }}
            />
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {t('feedbackBubble.pasteHint', { max: MAX_IMAGES })}
          </p>
        </div>

        <label className="flex cursor-pointer items-start gap-2 text-[13px]">
          <Checkbox checked={withDiagnostics} onCheckedChange={(v) => setWithDiagnostics(v === true)} className="mt-0.5" />
          <span>
            {t('feedbackBubble.diagnosticsLabel')}
            <span className="mt-0.5 block text-[11px] text-muted-foreground">
              {withDiagnostics
                ? t('feedbackBubble.diagnosticsDetail', {
                  pathname: pathname || '/',
                  browser: browserLabel(ua),
                  viewport,
                  errSuffix: nErr ? t('feedbackBubble.errSuffix', { n: nErr }) : '',
                })
                : t('feedbackBubble.diagnosticsDetailOff')}
            </span>
          </span>
        </label>

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={send.isPending}>{t('feedbackBubble.cancel')}</Button>
          <Button type="button" onClick={() => void submit()} disabled={!canSendFeedback(message) || send.isPending}>
            {send.isPending ? <Loader2 className="animate-spin" /> : <Send />} {t('feedbackBubble.submit')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
